<template>
  <v-card
    class="ma-0 px-1 app-form-page"
    :class="{
      'app-form-page--fixed': !isEmbedded && !isMobile,
      'app-form-page--mobile': isMobile && !isEmbedded,
      'app-form-page--embedded': isEmbedded,
    }"
    width="100%"
    flat
  >
    <!-- Mobile header -->
    <div v-if="isMobile && !isEmbedded" class="amf-header">
      <div class="amf-header__bar">
        <div class="amf-header__bar-actions">
          <button
            v-if="id"
            type="button"
            class="amf-header__btn amf-header__btn--create"
            :aria-label="$t(type)"
            @click="goTo(type)"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            v-if="!formType"
            type="button"
            class="amf-header__btn"
            :aria-label="$t('checkpageList')"
            @click="goTo('checkpageList')"
          >
            <v-icon size="20" color="#37474F">mdi-format-list-numbered</v-icon>
          </button>
        </div>
        <h1 class="amf-header__title">{{ $t('checkpage') }}</h1>
        <button
          v-if="!formType"
          type="button"
          class="amf-header__btn amf-header__btn--back"
          :aria-label="$t('back')"
          @click="$router.go(-1)"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
        <span
          v-else
          class="amf-header__btn amf-header__btn--back"
          style="visibility: hidden"
        />
      </div>
    </div>

    <!-- Desktop / embedded toolbar -->
    <v-app-bar
      v-else
      flat
      class="my-0 app-form-toolbar"
      :class="
        isMobile
          ? 'mb-2 py-1 px-2 app-form-toolbar--mobile'
          : 'mb-0 py-0 px-2'
      "
      dense
      :height="!isEmbedded && !isMobile ? 40 : undefined"
    >
      <v-toolbar-title class="app-form-toolbar__title me-3">
        {{ $t('checkpage') }}
      </v-toolbar-title>
      <v-spacer />
      <div class="app-form-toolbar__actions">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="id"
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo(type)"
            >
              <v-icon color="teal">mdi-plus-box-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(type) }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="!formType"
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo('checkpageList')"
            >
              <v-icon color="primary">mdi-format-list-numbered</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('checkpageList') }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="!formType"
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="$router.go(-1)"
            >
              <v-icon color="primary">mdi-arrow-left</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('back') }}</span>
        </v-tooltip>
      </div>
    </v-app-bar>

    <v-progress-linear
      :active="!render"
      :indeterminate="!render"
      absolute
      bottom
      style="top: -4px"
      color="primary"
    />

    <v-form
      v-if="render"
      ref="form"
      v-model="valid"
      class="app-form-page__form"
      lazy-validation
    >
      <div class="app-form-body checkpage-body">
        <v-container
          fluid
          class="app-form-container app-form-container--cards checkpage-container pa-3"
        >
          <v-row class="app-form-card-grid checkpage-grid ma-0" dense>
            <v-col cols="12" class="pa-2 checkpage-col">
              <div class="app-form-card checkpage-card">
                <div class="app-form-card__header">
                  <div class="app-form-card__icon checkpage-card__icon" aria-hidden="true">
                    <v-icon color="primary" size="24">mdi-checkbook</v-icon>
                  </div>
                  <div class="app-form-card__heading">
                    <div class="app-form-card__title">
                      {{ $t('newChekpage') }}
                    </div>
                    <div class="app-form-card__subtitle">
                      {{ bankDisplayName }}
                    </div>
                  </div>
                </div>

                <div class="app-form-card__body app-form-card__body--stack checkpage-card__body">
                  <v-row class="app-form-fields checkpage-fields ma-0">
                    <v-col cols="12" sm="4" class="checkpage-field">
                      <autocomplete
                        v-model="bank"
                        :rules="[v => !!v || $t('is-required')]"
                        :title="$t('bank')"
                        type="bank"
                        :set-init="true"
                        :value="bank"
                      />
                    </v-col>
                    <v-col cols="12" sm="4" class="checkpage-field">
                      <v-text-field
                        :value="toPersianDigits(count)"
                        :label="$t('count')"
                        dense
                        :rules="[v => !!v || $t('is-required')]"
                        outlined
                        clearable
                        hide-details="auto"
                        @input="onCountInput"
                      />
                    </v-col>
                    <v-col cols="12" sm="4" class="checkpage-field">
                      <v-text-field
                        :value="toPersianDigits(firstnumber)"
                        :label="$t('firstNumberPage')"
                        dense
                        :rules="[v => !!v || $t('is-required')]"
                        outlined
                        clearable
                        hide-details="auto"
                        @input="onFirstnumberInput"
                      />
                    </v-col>
                  </v-row>

                  <div
                    class="checkpage-summary"
                    :class="{ 'checkpage-summary--ready': !!lastnumber }"
                  >
                    <span
                      v-if="count"
                      class="checkpage-summary__pill"
                    >
                      {{ toPersianDigits(count) }} {{ $t('count') }}
                    </span>
                    <div class="checkpage-summary__parts">
                      <div class="checkpage-summary__part">
                        <span class="checkpage-summary__part-label">از</span>
                        <strong class="checkpage-summary__part-value">{{ formatFaNum(firstnumber) }}</strong>
                      </div>
                      <span class="checkpage-summary__sep" aria-hidden="true">—</span>
                      <div class="checkpage-summary__part">
                        <span class="checkpage-summary__part-label">تا</span>
                        <strong class="checkpage-summary__part-value checkpage-summary__part-value--end">{{ formatFaNum(lastnumber) }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <div class="app-form-page__actions">
        <div class="app-form-page__actions-inner">
          <v-btn
            v-if="!id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="create"
          >
            {{ $t('submit') }}
          </v-btn>
          <v-btn
            v-if="id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="update"
          >
            {{ $t('submit') }}
          </v-btn>
          <v-btn
            v-if="!formType"
            color="grey"
            outlined
            depressed
            class="app-form-action-btn"
            @click="goTo('checkpageList')"
          >
            {{ $t('checkpageList') }}
          </v-btn>
        </div>
      </div>
    </v-form>

    <v-snackbar
      v-model="snakbar"
      :color="(snakbarColor ? snakbarColor : 'primary')"
      top
      style="z-index: 1000000000000 !important;"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snakbar = !snakbar"
        >
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
  // import aicomplete from '../../components/general/AIcomplete/index'
  import General from '../../../../Mixins/general'
  export default {
    name: 'Product',
    components: {
      autocomplete: () => import('../../components/general/patial/autocomplete'),
    },
    mixins: [General],
    props: ['formType'],
    data: () => ({
      options: {
        locale: 'en-US',
        prefix: '',
        suffix: ' ',
        precision: 0,
      },
      render: false,
      isLoading: false,
      alert: false,
      firstnumber: null,
      type: null,
      bank: null,
      count: null,
      valid: null,
      name: null,
      id: null,
      loading: false,
    }),
    computed: {
      isEmbedded () {
        return !!this.formType
      },
      bankDisplayName () {
        if (!this.bank) return '—'
        return this.bank.name || this.bank.title || this.bank.label || '—'
      },
      lastnumber () {
        if (this.count && this.firstnumber) {
          return parseFloat(this.count) + parseFloat(this.firstnumber) - parseFloat(1)
        } else {
          return ''
        }
      },
    },
    watch: {
      defaultCurrency () {
        if (this.defaultCurrency && this.$refs.refCurrency) {
          this.$refs.refCurrency.setModel(this.defaultCurrency)
          this.currency_amount = this.defaultCurrency.amount
        }
      },
      currency (val, old) {
        if (val) {
          this.currency_amount = val.amount
          this.options.suffix = val.symbol
        } else {
          this.options.suffix = ' '
          this.currency_amount = null
        }
      },
    },
    beforeMount () {
      if (this.$route.params.id != null) {
        this.show()
        this.id = this.$route.params.id
        this.render = false
      } else {
        this.render = true
        this.defaultCurrency = this.getDefault('defaultCurrency')
      }
    },
    mounted () {
      if (this.formType) {
        this.type = this.formType
      }
    },
    methods: {
      create () {
        var app = this
        app.alert = false
        app.isLoading = true
        // app.currency = app.defaultCurrency
        app.$axios.post(this.$endpoint + 'checkpage', app.$data)
          .then(res => {
            app.snakbarText = app.$t('registered')
            app.snakbarColor = 'green'
            app.snakbar = true
            if (app.id) {
            } else {
              app.$emit('input', res.data.data)
            }
            app.goTo('checkpageList')
          })
          .catch(err => {
            this.serverErrorHandeler(err)
          })
          .finally(() => {
            this.isLoading = false
            // this.clearForm()
          })
      },
      show () {
        var app = this
        app.clearForm()
        app.loading = true
        app.clearing = true
        app.showing = true
        app.$axios.get(this.$endpoint + 'bank/' + this.$route.params.id + '/edit', {
          params: {
            id: this.$route.params.id,
          },
        })
          .then(res => {
            res = res.data
            app.$nextTick(() => {
              app.type = res.type
              app.typeForm = res.type
              app.id = res.id
              app.name = res.name
              app.number = res.number
              app.initial_price = res.initial_price
              app.currency = res.currency
              app.currency_amount = res.currency_amount
              app.content = res.content
              if (res.tags) {
                app.tag = res.tags
              }
              app.render = true
            })
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      update () {
        var app = this
        app.isLoading = true
        app.$axios.put(this.$endpoint + 'bank/' + this.$route.params.id, this.$data)
          .then(res => {
            res = res.data
            app.$emit('close', app.$route)
            app.goTo(app.type + 'List')
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.isLoading = false))
      },
      clearForm () {
        this.firstnumber = null
        this.bank = null
        this.count = null
        this.valid = null
        this.name = null
        this.id = null
      },
      toPersianDigits (value) {
        if (value === null || value === undefined || value === '') return ''
        return String(value).replace(/\d/g, function (d) {
          return '۰۱۲۳۴۵۶۷۸۹'[d]
        })
      },
      toEnglishDigits (value) {
        if (value === null || value === undefined || value === '') return value
        return String(value)
          .replace(/[۰-۹]/g, function (d) {
            return '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)
          })
          .replace(/[٠-٩]/g, function (d) {
            return '٠١٢٣٤٥٦٧٨٩'.indexOf(d)
          })
      },
      formatFaNum (value) {
        if (value === null || value === undefined || value === '') return '—'
        return this.toPersianDigits(value)
      },
      onCountInput (val) {
        this.count = (val === null || val === undefined || val === '')
          ? null
          : this.toEnglishDigits(val)
      },
      onFirstnumberInput (val) {
        this.firstnumber = (val === null || val === undefined || val === '')
          ? null
          : this.toEnglishDigits(val)
      },
    },
  }
</script>

<style scoped>
.checkpage-card {
  min-height: 100%;
}

.checkpage-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(91, 124, 153, 0.14);
}

.checkpage-card__body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.checkpage-summary {
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  padding: 12px 14px;
  border-radius: 8px;
  background: rgba(91, 124, 153, 0.06);
  border: 1px solid rgba(91, 124, 153, 0.12);
}

.checkpage-summary--ready {
  background: rgba(46, 125, 50, 0.06);
  border-color: rgba(46, 125, 50, 0.18);
}

.checkpage-summary__pill {
  flex: 0 0 auto;
  font-size: 0.78rem;
  font-weight: 600;
  color: #fff;
  background: #5b7c99;
  border-radius: 999px;
  padding: 4px 12px;
}

.checkpage-summary--ready .checkpage-summary__pill {
  background: #2e7d32;
}

.checkpage-summary__parts {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
}

.checkpage-summary__part {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(91, 124, 153, 0.12);
}

.checkpage-summary__part-label {
  font-size: 0.72rem;
  color: rgba(47, 69, 88, 0.6);
}

.checkpage-summary__part-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2f4558;
  font-variant-numeric: tabular-nums;
}

.checkpage-summary__part-value--end {
  color: #2e7d32;
}

.checkpage-summary__sep {
  color: rgba(91, 124, 153, 0.45);
  flex: 0 0 auto;
}

.theme--dark .checkpage-summary {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .checkpage-summary__part {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .checkpage-summary__part-label {
  color: rgba(255, 255, 255, 0.55);
}

.theme--dark .checkpage-summary__part-value {
  color: rgba(255, 255, 255, 0.9);
}

.theme--dark .checkpage-summary__part-value--end {
  color: #81c784;
}

@media (max-width: 799px) {
  .checkpage-fields.checkpage-fields {
    display: flex !important;
    flex-direction: column !important;
    flex-wrap: nowrap !important;
    margin: 0 !important;
    gap: 28px !important;
  }

  .checkpage-field.checkpage-field {
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 auto !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .app-form-page--mobile .checkpage-summary {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .app-form-page--mobile .checkpage-summary__parts {
    width: 100%;
    justify-content: space-between;
  }

  .app-form-page--mobile .checkpage-summary__part {
    flex: 1 1 0;
    justify-content: space-between;
  }

  .app-form-page--mobile .checkpage-body.app-form-body {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 1 0 !important;
    min-height: 0 !important;
    height: 100% !important;
    overflow: hidden !important;
    padding: 10px 12px !important;
    background: #f3f5f7 !important;
  }

  .app-form-page--mobile .checkpage-container.app-form-container--cards {
    flex: 1 1 0 !important;
    min-height: 0 !important;
    height: 100% !important;
    max-height: none !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 0 !important;
  }

  .app-form-page--mobile .checkpage-grid.app-form-card-grid {
    flex: 1 1 0 !important;
    min-height: 0 !important;
    height: 100% !important;
    max-height: none !important;
    margin: 0 !important;
    align-items: stretch !important;
  }

  .app-form-page--mobile .checkpage-col {
    flex: 1 1 0 !important;
    min-height: 0 !important;
    height: 100% !important;
    max-height: none !important;
    max-width: 100% !important;
    width: 100% !important;
    padding: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .app-form-page--mobile .checkpage-card.app-form-card {
    flex: 1 1 0 !important;
    height: 100% !important;
    min-height: 0 !important;
    max-height: none !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .app-form-page--mobile .checkpage-card__body {
    flex: 1 1 0 !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .app-form-page--mobile .checkpage-summary {
    margin-top: auto !important;
  }
}
</style>
